/**
 * ALBERO core utilities.
 *
 * - Cart API: every add-to-cart in ALBERO goes through `addToCart()`, which talks to the
 *   real Shopify AJAX cart and emits Shopify's standard `CartLinesUpdateEvent` so that the
 *   theme's global cart drawer, cart icons and analytics stay in sync.
 * - AlberoDialog: accessible drawer / modal base element (focus trap, ESC, scroll lock,
 *   focus restoration, overlay close).
 * - Small helpers: live-region announcements, reduced-motion detection.
 */

export const config = window.AlberoConfig || { routes: {}, strings: {} };

const FOCUSABLE =
  'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

export const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Screen-reader announcement through the global live region. */
export function announce(message) {
  const region = document.getElementById('albero-live-region');
  if (!region || !message) return;
  region.textContent = '';
  window.setTimeout(() => {
    region.textContent = message;
  }, 60);
}

export function formatString(template, values = {}) {
  return String(template || '')
    .replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => (key in values ? values[key] : ''))
    .replace(/__(\w+?)__/g, (match, key) => (key in values ? values[key] : match));
}

/* -------------------------------------------------------------------------- */
/* Scroll lock                                                                 */
/* -------------------------------------------------------------------------- */

let scrollLocks = 0;
export function lockScroll() {
  scrollLocks += 1;
  if (scrollLocks === 1) {
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--albero-scrollbar-gap', `${scrollbar}px`);
    document.documentElement.classList.add('albero-scroll-locked');
  }
}
export function unlockScroll() {
  scrollLocks = Math.max(0, scrollLocks - 1);
  if (scrollLocks === 0) document.documentElement.classList.remove('albero-scroll-locked');
}

export function getFocusable(container) {
  return [...container.querySelectorAll(FOCUSABLE)].filter(
    (el) => el.offsetParent !== null || el === document.activeElement
  );
}

/* -------------------------------------------------------------------------- */
/* Dialog / drawer base                                                        */
/* -------------------------------------------------------------------------- */

/**
 * <albero-dialog id="…" class="albero-dialog albero-dialog--left|right|sheet|center">
 *   <div class="albero-dialog__overlay" data-albero-close></div>
 *   <div class="albero-dialog__panel" role="dialog" aria-modal="true" aria-labelledby="…" tabindex="-1">…</div>
 * </albero-dialog>
 *
 * Open with `el.open(triggerElement)` or any `[data-albero-open="id"]` button.
 */
export class AlberoDialog extends HTMLElement {
  #previousFocus = null;
  #onKeydown = (event) => this.#handleKeydown(event);

  connectedCallback() {
    this.panel = this.querySelector('.albero-dialog__panel');
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-albero-close]')) {
        event.preventDefault();
        this.close();
      }
    });
    if (!this.hasAttribute('open')) this.panel?.setAttribute('inert', '');
  }

  disconnectedCallback() {
    if (this.isOpen) {
      document.removeEventListener('keydown', this.#onKeydown);
      unlockScroll();
    }
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  open(trigger = document.activeElement) {
    if (this.isOpen) return;
    this.#previousFocus = trigger instanceof HTMLElement ? trigger : null;
    this.panel?.removeAttribute('inert');
    this.setAttribute('open', '');
    lockScroll();
    document.addEventListener('keydown', this.#onKeydown);
    this.#setTriggersExpanded(true);
    window.requestAnimationFrame(() => {
      const target = this.querySelector('[data-albero-autofocus]') || getFocusable(this.panel)[0] || this.panel;
      target?.focus({ preventScroll: true });
    });
    this.dispatchEvent(new CustomEvent('albero:dialog-open', { bubbles: true }));
  }

  close({ restoreFocus = true } = {}) {
    if (!this.isOpen) return;
    this.removeAttribute('open');
    this.panel?.setAttribute('inert', '');
    unlockScroll();
    document.removeEventListener('keydown', this.#onKeydown);
    this.#setTriggersExpanded(false);
    if (restoreFocus && this.#previousFocus?.isConnected) this.#previousFocus.focus({ preventScroll: true });
    this.dispatchEvent(new CustomEvent('albero:dialog-close', { bubbles: true }));
  }

  toggle(trigger) {
    this.isOpen ? this.close() : this.open(trigger);
  }

  #setTriggersExpanded(expanded) {
    if (!this.id) return;
    document
      .querySelectorAll(`[aria-controls="${this.id}"]`)
      .forEach((trigger) => trigger.setAttribute('aria-expanded', String(expanded)));
  }

  #handleKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'Tab' || !this.panel) return;
    const focusable = getFocusable(this.panel);
    if (!focusable.length) {
      event.preventDefault();
      this.panel.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!this.panel.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('albero-dialog')) customElements.define('albero-dialog', AlberoDialog);

/* Global openers: <button data-albero-open="dialog-id" aria-controls="dialog-id"> */
document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-albero-open]');
  if (!opener) return;
  const dialog = document.getElementById(opener.getAttribute('data-albero-open'));
  if (dialog instanceof AlberoDialog) {
    event.preventDefault();
    dialog.open(opener);
  }
});

/* -------------------------------------------------------------------------- */
/* Cart                                                                        */
/* -------------------------------------------------------------------------- */

let standardEvents = null;
async function loadStandardEvents() {
  if (standardEvents !== null) return standardEvents;
  try {
    standardEvents = await import('@shopify/events');
  } catch (error) {
    standardEvents = false;
  }
  return standardEvents;
}
loadStandardEvents();

function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((el) => el.dataset.sectionId)
    .filter((id, index, all) => id && all.indexOf(id) === index);
}

export async function getCart() {
  const response = await fetch(`${config.routes.cart}.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
  return response.json();
}

export class CartError extends Error {
  constructor(message, details) {
    super(message);
    this.name = 'CartError';
    this.details = details;
  }
}

/**
 * Adds lines to the Shopify cart.
 *
 * @param {FormData | Array<{id: string|number, quantity: number, selling_plan?: string, properties?: object}>} input
 * @param {{ source?: Element, context?: 'product'|'dialog' }} [options]
 * @returns {Promise<object>} the updated AJAX cart
 */
export async function addToCart(input, { source = document, context = 'product' } = {}) {
  const events = await loadStandardEvents();
  const sections = cartSectionIds();
  let body;
  let headers;
  let lines;

  if (input instanceof FormData) {
    const formData = input;
    if (sections.length) formData.set('sections', sections.join(','));
    formData.set('sections_url', window.location.pathname);
    body = formData;
    headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
    lines = [{ merchandiseId: String(formData.get('id')), quantity: Number(formData.get('quantity') || 1) }];
  } else {
    const items = input.map((item) => {
      const line = { id: Number(item.id), quantity: Number(item.quantity || 1) };
      if (item.selling_plan) line.selling_plan = item.selling_plan;
      if (item.properties) line.properties = item.properties;
      return line;
    });
    body = JSON.stringify({ items, sections: sections.join(','), sections_url: window.location.pathname });
    headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
    lines = items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity }));
  }

  const itemCount = lines.reduce((total, line) => total + line.quantity, 0);
  const deferred = events ? events.CartLinesUpdateEvent.createPromise() : null;

  if (events && deferred) {
    source.dispatchEvent(
      new events.CartLinesUpdateEvent({ action: 'add', context, lines, promise: deferred.promise })
    );
  }

  let response;
  let data;
  try {
    response = await fetch(config.routes.cartAdd, { method: 'POST', headers, body });
    data = await response.json();
  } catch (error) {
    deferred?.reject(error);
    if (events) {
      source.dispatchEvent(
        new events.CartErrorEvent({ error: error?.message || 'Network error', code: 'SERVICE_UNAVAILABLE' })
      );
    }
    throw new CartError(config.strings.addError, error);
  }

  if (!response.ok || data.status) {
    const message = data.description || data.message || config.strings.addError;
    if (events) {
      source.dispatchEvent(
        new events.CartErrorEvent({ error: message, code: 'INVALID', detail: { description: data.description } })
      );
    }
    try {
      const cart = await getCart();
      deferred?.resolve({
        cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart.items, source: 'albero', itemCount },
      });
      document.dispatchEvent(new CustomEvent('albero:cart-updated', { detail: { cart } }));
    } catch (error) {
      deferred?.reject(error);
    }
    throw new CartError(message, data);
  }

  const cart = await getCart();
  deferred?.resolve({
    cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: { items: cart.items, sections: data.sections, didError: false, source: 'albero', itemCount },
  });
  document.dispatchEvent(new CustomEvent('albero:cart-updated', { detail: { cart } }));
  announce(config.strings.added);

  // Without the standard-events bundle the theme drawer can't refresh itself: open the cart page.
  if (!events) window.location.href = config.routes.cart;
  return cart;
}

export function openCart() {
  if (window.Shopify?.actions?.openCart) {
    window.Shopify.actions.openCart();
    return;
  }
  const drawer = document.querySelector('theme-drawer#cart-drawer');
  if (drawer && typeof drawer.open === 'function') drawer.open();
  else window.location.href = config.routes.cart;
}

/* -------------------------------------------------------------------------- */
/* Cart count (header badge)                                                   */
/* -------------------------------------------------------------------------- */

class AlberoCartCount extends HTMLElement {
  #onLinesUpdate = (event) => {
    event.promise
      ?.then(({ cart }) => {
        if (cart && typeof cart.totalQuantity === 'number') this.render(cart.totalQuantity);
      })
      .catch(() => {});
  };
  #onAlberoUpdate = (event) => {
    const cart = event.detail?.cart;
    if (cart) this.render(cart.item_count);
  };
  #onPageShow = (event) => {
    if (event.persisted) getCart().then((cart) => this.render(cart.item_count)).catch(() => {});
  };

  connectedCallback() {
    document.addEventListener('shopify:cart:lines-update', this.#onLinesUpdate);
    document.addEventListener('albero:cart-updated', this.#onAlberoUpdate);
    window.addEventListener('pageshow', this.#onPageShow);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:cart:lines-update', this.#onLinesUpdate);
    document.removeEventListener('albero:cart-updated', this.#onAlberoUpdate);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  render(count) {
    const value = Number(count) || 0;
    this.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = value > 99 ? '99+' : String(value);
    });
    this.toggleAttribute('data-empty', value === 0);
    const button = this.querySelector('[data-cart-label]');
    if (button) {
      button.setAttribute('aria-label', formatString(button.dataset.cartLabel, { count: value }));
    }
  }
}

if (!customElements.get('albero-cart-count')) customElements.define('albero-cart-count', AlberoCartCount);

/* Cart openers: any [data-albero-cart-open] link opens the drawer (falls back to /cart). */
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-albero-cart-open]');
  if (!trigger) return;
  const drawer = document.querySelector('theme-drawer#cart-drawer');
  if (!drawer && !window.Shopify?.actions?.openCart) return; // let the link navigate to the cart page
  event.preventDefault();
  openCart();
});